package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.explain.Breakdown
import com.evoapps.evoshift.domain.explain.Figure
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.explain.Sign
import com.evoapps.evoshift.domain.explain.Term
import com.evoapps.evoshift.domain.explain.TermKind
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.number
import kotlinx.datetime.plus

/*
 * SH-115: a labour budget. The workplace's financial managers set how much employer labour cost a
 * month may take (and, optionally, a week); the roster builder shows the planned cost against it
 * while the month is built, the shift dialogs show what a change does to it, and the Money tab
 * compares it with the planned and actual labour cost. Over budget only warns, it never blocks.
 *
 * The planned cost is the same figure as everywhere else: the employer cost of the cost snapshots
 * (shift_financials) of the assigned shifts. A shift without a snapshot yet (just added or handed
 * to someone else) counts at its person's cost per hour from their snapshots of the month, or the
 * month's average (a month with none yet: the team's from their pay terms), until its own snapshot
 * is written. An unassigned shift still to be filled counts
 * as an estimate (OpenShifts.kt): the month's average cost per hour, else the team's from their pay
 * terms; without either it is not priced and the status says how many such shifts there are.
 */

/**
 * A workplace's labour budget (`workplaces/{id}/labour_budget/data`): employer labour cost per
 * month, optionally also per week. Off ([isOn] false) until a monthly amount is set; a weekly one
 * only counts with a monthly one.
 */
data class LabourBudget(
    /** Employer labour cost a roster month may take, in the workplace's currency; null = off. */
    val monthly: Double? = null,
    /** Employer labour cost a week may take; null = no weekly budget. */
    val weekly: Double? = null
) {
    val isOn: Boolean get() = monthly != null

    /** The budget as it is kept: no weekly amount without a monthly one. */
    fun normalized(): LabourBudget = if (monthly == null) OFF else this

    companion object {
        val OFF = LabourBudget()
        /** The largest amount the settings accept (and firestore.rules). */
        const val MAX_AMOUNT = 100_000_000.0

        fun isValidAmount(amount: Double): Boolean = amount > 0 && amount <= MAX_AMOUNT && !amount.isNaN()
    }
}

/** A budget and what is planned against it. */
data class BudgetUse(val budget: Double, val used: Double) {
    /** What is left; negative once over. */
    val remaining: Double get() = budget - used

    /** Over by more than half a grosz (rounding of summed snapshots never flips it). */
    val isOver: Boolean get() = used - budget > MONEY_EPSILON

    /** [used] as a share of [budget] (1.0 = all of it), for the bar; 0 without a budget. */
    val share: Double get() = if (budget > 0) used / budget else 0.0

    companion object {
        const val MONEY_EPSILON = 0.005
    }
}

/**
 * One week of the month against the weekly budget. A week the month cuts (it starts before the
 * 1st or ends after the last day) gets the share of the weekly budget for its days in the month.
 */
data class WeekBudgetUse(
    /** The week's first day in the month. */
    val start: LocalDate,
    /** The week's last day in the month. */
    val end: LocalDate,
    val use: BudgetUse
) {
    operator fun contains(date: LocalDate): Boolean = date in start..end
}

/** The month's planned labour cost against the budget, and per week when there is a weekly one. */
data class LabourBudgetStatus(
    val budget: LabourBudget,
    val month: BudgetUse,
    /** Empty without a weekly budget. */
    val weeks: List<WeekBudgetUse>,
    /** The month's unassigned shifts: in [month] at their estimate when priced, else left out. */
    val open: OpenShiftsCost = OpenShiftsCost.NONE
) {
    fun weekOf(date: LocalDate): WeekBudgetUse? = weeks.firstOrNull { date in it }

    /** What adding [delta] of labour cost on [date] leaves: the month's and the week's remaining. */
    fun effectOf(date: LocalDate, delta: Double): BudgetEffect {
        val week = weekOf(date)
        return BudgetEffect(
            delta = delta,
            month = month.copy(used = month.used + delta),
            week = week?.let { it.copy(use = it.use.copy(used = it.use.used + delta)) }
        )
    }

    val isOver: Boolean get() = month.isOver || weeks.any { it.use.isOver }
}

/** A planned change's effect: its cost ([delta], + more, − less) and the budgets after it. */
data class BudgetEffect(
    val delta: Double,
    val month: BudgetUse,
    val week: WeekBudgetUse?
) {
    /** Over the month's or the week's budget after the change: a warning, never a block. */
    val isOver: Boolean get() = month.isOver || week?.use?.isOver == true
}

/** The month's budget against its planned and actual labour cost (the Money tab). */
data class BudgetComparison(
    val budget: Double,
    val planned: BudgetUse,
    /** Null until any labour cost is actual (nobody clocked, no takings entered). */
    val actual: BudgetUse?
) {
    companion object {
        /** Null when the budget is off. */
        fun of(budget: LabourBudget, planned: Double, actual: Double?): BudgetComparison? {
            val monthly = budget.monthly ?: return null
            return BudgetComparison(monthly, BudgetUse(monthly, planned), actual?.takeIf { it > 0 }?.let { BudgetUse(monthly, it) })
        }
    }
}

/**
 * What the shifts of a month cost and what a change would cost, from the month's cost snapshots:
 * a snapshot of the person who works the shift is its cost; otherwise the person's cost per hour
 * in their snapshots of the month, else the month's average cost per hour.
 */
class ShiftCostEstimate(
    /** The employer cost of each shift's snapshot, kept only when it is the assignee's. */
    private val snapshotCost: Map<String, Double>,
    /** The snapshot's assignee per shift (a snapshot of someone else is stale, SH-12). */
    private val snapshotUser: Map<String, String?>,
    val costPerHourByUser: Map<String, Double>,
    val averageCostPerHour: Double?,
    /** People whose shifts are not paid work here (an unpaid owner). */
    private val unpaidUserIds: Set<String> = emptySet(),
    /**
     * What an unassigned shift is estimated at per hour: the month's average, else the team's from
     * their pay terms ([TeamCostRate]); null when neither is known (such shifts are not priced).
     */
    val openCostPerHour: Double? = averageCostPerHour,
    /** SH-152: the cost snapshots themselves, by shift id: what a shift's cost is made of. */
    private val snapshotDetail: Map<String, ShiftFinancials> = emptyMap(),
    /** SH-206: people who do not count in the month's labour cost ([LabourInclusion]): their shifts add 0. */
    private val excludedUserIds: Set<String> = emptySet(),
    /** SH-232: shifts on days their people work without pay (UnpaidWork): they add 0. */
    private val unpaidShiftIds: Set<String> = emptySet()
) {
    /** SH-152: whether [userId]'s shifts are not paid work here (an unpaid owner). */
    fun isUnpaid(userId: String): Boolean = userId in unpaidUserIds && userId !in excludedUserIds

    /** SH-206: whether [userId] does not count in the month's labour cost (their shifts add 0). */
    fun isExcluded(userId: String): Boolean = userId in excludedUserIds

    /** Their shifts add nothing to the labour cost: not counted, or working without pay. */
    private fun addsNothing(userId: String): Boolean = userId in excludedUserIds || userId in unpaidUserIds

    /**
     * SH-152: how [costAs] works out what [shift] (null: a new one) costs as [userId] working
     * [hours]: its cost snapshot's parts, the person's own cost per hour, or the month's (the
     * team's) average; null when it cannot be estimated. The terms add up to [costAs].
     */
    fun explain(shift: Shift?, userId: String?, hours: Double): Breakdown? {
        val shown = costAs(shift, userId, hours) ?: return null
        val terms = if (shift != null && shift.userId == userId) {
            val hoursBefore = hoursOf(shift)
            val before = costOf(shift)
            when {
                hoursBefore == hours -> currentTerms(shift)
                hoursBefore > 0 && before > 0 -> listOf(Term(TermKind.HOURS_AT_OWN_RATE, shown, hours = hours, rate = before / hoursBefore))
                else -> rateTerms(userId, hours)
            }
        } else rateTerms(userId, hours)
        return Breakdown.of(Figure.SHIFT_COST, Side.PLAN, shown, terms)
    }

    /** What [costOf] ([shift]) is made of. */
    private fun currentTerms(shift: Shift): List<Term> {
        val userId = shift.userId ?: return if (shift.isUnfilledDemand) rateTerms(null, hoursOf(shift)) else emptyList()
        if (addsNothing(userId)) return rateTerms(userId, hoursOf(shift))
        if (shift.id in unpaidShiftIds && userId !in excludedUserIds) return listOf(Term(TermKind.PERSON_UNPAID, 0.0, personId = userId, hours = hoursOf(shift)))
        val cost = snapshotCost[shift.id]
        val owner = snapshotUser[shift.id]
        if (cost == null || (owner != null && owner != userId)) return rateTerms(userId, hoursOf(shift))
        val detail = snapshotDetail[shift.id] ?: return listOf(Term(TermKind.SHIFT_SNAPSHOT, cost, hours = hoursOf(shift)))
        val gross = detail.costs.estimatedPayout
        val night = detail.costs.nightAllowance
        return listOf(
            Term(
                TermKind.SHIFT_GROSS, gross - night,
                hours = detail.ratesSnapshot.plannedHours.takeIf { it > 0 },
                rate = detail.ratesSnapshot.rateUsed.takeIf { it > 0 }
            ),
            Term(TermKind.NIGHT_ALLOWANCE, night, hours = detail.ratesSnapshot.nightHours.takeIf { it > 0 }),
            Term(TermKind.EMPLOYER_CONTRIBUTIONS, cost - gross)
        )
    }

    /** [hours] at the cost per hour [costOf] ([userId], [hours]) uses. */
    private fun rateTerms(userId: String?, hours: Double): List<Term> {
        if (userId != null && userId in excludedUserIds) return listOf(Term(TermKind.PERSON_NOT_COUNTED, 0.0, Sign.NOT_COUNTED, personId = userId, hours = hours))
        if (userId != null && userId in unpaidUserIds) return listOf(Term(TermKind.PERSON_UNPAID, 0.0, personId = userId, hours = hours))
        val own = userId?.let { costPerHourByUser[it] }
        if (own != null) return listOf(Term(TermKind.HOURS_AT_OWN_RATE, own * hours, personId = userId, hours = hours, rate = own))
        val rate = openCostPerHour ?: return emptyList()
        val kind = if (averageCostPerHour != null) TermKind.HOURS_AT_MONTH_AVERAGE else TermKind.HOURS_AT_TEAM_AVERAGE
        return listOf(Term(kind, rate * hours, hours = hours, rate = rate))
    }

    /**
     * Employer cost of [userId] working [hours]: 0 for an unpaid person; for nobody (an unassigned
     * shift) the estimate at [openCostPerHour]; null when no cost per hour is known.
     */
    fun costOf(userId: String?, hours: Double): Double? = when {
        userId == null -> openCostPerHour?.times(hours)
        addsNothing(userId) -> 0.0
        // Without their own snapshots: the month's average, else (a month with none) the team's.
        else -> (costPerHourByUser[userId] ?: openCostPerHour)?.times(hours)
    }

    /**
     * What [shift] costs as it is: its snapshot when it is its assignee's, else the estimate; an
     * unassigned shift still to be filled at [openCostPerHour]. 0 when unknown.
     */
    fun costOf(shift: Shift): Double {
        val userId = shift.userId ?: return if (shift.isUnfilledDemand) costOf(null, hoursOf(shift)) ?: 0.0 else 0.0
        if (addsNothing(userId) || shift.id in unpaidShiftIds) return 0.0
        snapshotCost[shift.id]?.let { cost ->
            val owner = snapshotUser[shift.id]
            if (owner == null || owner == userId) return cost
        }
        return costOf(userId, hoursOf(shift)) ?: 0.0
    }

    /**
     * The cost change of [shift] (null: a new one) becoming [userId] working [hours] (null: nobody,
     * priced as an unassigned shift): null when it cannot be estimated (no cost per hour known).
     */
    fun deltaOf(shift: Shift?, userId: String?, hours: Double): Double? {
        val before = shift?.let { costOf(it) } ?: 0.0
        if (shift != null && shift.userId == userId) {
            // The same person: their own cost scales with the hours (an unchanged shift costs nothing more).
            val hoursBefore = hoursOf(shift)
            if (hoursBefore == hours) return 0.0
            if (hoursBefore > 0 && before > 0) return before * hours / hoursBefore - before
        }
        val after = costOf(userId, hours) ?: return null
        return after - before
    }

    /**
     * What [shift] costs by its own pricing, for its card: its snapshot (stored, or priced on the
     * fly from the pay terms), 0 for an unpaid person, an unassigned one's estimate; null when it
     * is not priced (someone without pay rates) or cannot be estimated.
     */
    fun pricedCostOf(shift: Shift): Double? {
        val userId = shift.userId ?: return if (shift.isUnfilledDemand) costOf(null, hoursOf(shift)) else null
        if (addsNothing(userId) || shift.id in unpaidShiftIds) return 0.0
        val cost = snapshotCost[shift.id] ?: return null
        val owner = snapshotUser[shift.id]
        return cost.takeIf { owner == null || owner == userId }
    }

    /**
     * What [shift] (null: a new one) costs as [userId] (null: nobody) working [hours]: its cost now
     * plus the change; null when it cannot be estimated.
     */
    fun costAs(shift: Shift?, userId: String?, hours: Double): Double? =
        deltaOf(shift, userId, hours)?.let { delta -> (shift?.let(::costOf) ?: 0.0) + delta }

    companion object {
        val EMPTY = ShiftCostEstimate(emptyMap(), emptyMap(), emptyMap(), null)

        fun hoursOf(shift: Shift): Double = calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end)

        /** From the month's [shifts] and their cost [snapshots]. */
        /**
         * From the month's [shifts] and their cost [snapshots]; [teamCostPerHour] prices shifts
         * (unassigned ones, and assigned ones not priced yet) while the month has no snapshot to average.
         */
        fun of(
            shifts: List<Shift>,
            snapshots: List<ShiftFinancials>,
            unpaidUserIds: Set<String> = emptySet(),
            teamCostPerHour: Double? = null,
            /** SH-193: the workplace's rate for unassigned shifts ([OpenShiftRates]); null: the month's average, else the team's. */
            openRate: OpenShiftRate? = null,
            /** SH-206: people who do not count in the month's labour cost: 0, and not in the average. */
            excludedUserIds: Set<String> = emptySet(),
            /** SH-232: shifts on days their people work without pay: 0, and not in the average. */
            unpaidShiftIds: Set<String> = emptySet()
        ): ShiftCostEstimate {
            val byId = shifts.associateBy { it.id }
            // Per person: their snapshots' employer cost over the hours priced.
            val perUser = mutableMapOf<String, Pair<Double, Double>>()
            snapshots.forEach { snapshot ->
                val userId = snapshot.userId ?: byId[snapshot.shiftId]?.userId ?: return@forEach
                if (userId in unpaidUserIds || userId in excludedUserIds || snapshot.shiftId in unpaidShiftIds) return@forEach
                val hours = snapshot.ratesSnapshot.plannedHours.takeIf { it > 0 }
                    ?: byId[snapshot.shiftId]?.let(::hoursOf) ?: return@forEach
                if (hours <= 0) return@forEach
                val (cost, priced) = perUser[userId] ?: (0.0 to 0.0)
                perUser[userId] = (cost + snapshot.costs.employerCost) to (priced + hours)
            }
            val totalCost = perUser.values.sumOf { it.first }
            val totalHours = perUser.values.sumOf { it.second }
            val average = if (totalHours > 0) totalCost / totalHours else null
            return ShiftCostEstimate(
                snapshotCost = snapshots.associate { it.shiftId to it.costs.employerCost },
                snapshotUser = snapshots.associate { it.shiftId to it.userId },
                costPerHourByUser = perUser.filterValues { it.second > 0 }.mapValues { (_, v) -> v.first / v.second },
                averageCostPerHour = average,
                unpaidUserIds = unpaidUserIds,
                openCostPerHour = if (openRate != null) openRate.perHour else average ?: teamCostPerHour,
                snapshotDetail = snapshots.associateBy { it.shiftId },
                excludedUserIds = excludedUserIds,
                unpaidShiftIds = unpaidShiftIds
            )
        }
    }
}

object LabourBudgetRules {
    /** The days of [year]/[month]. */
    fun daysOf(year: Int, month: Int): List<LocalDate> =
        generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month.number == month }.toList()

    /**
     * The month's weeks (starting on [firstDayOfWeek], ISO 1-7), cut to the month: first and last
     * day in the month of each.
     */
    fun weeksOf(year: Int, month: Int, firstDayOfWeek: Int): List<ClosedRange<LocalDate>> {
        val first = firstDayOfWeek.takeIf { it in 1..7 } ?: 1
        return daysOf(year, month)
            .fold(mutableListOf<MutableList<LocalDate>>()) { weeks, day ->
                if (weeks.isEmpty() || day.dayOfWeek.isoDayNumber == first) weeks += mutableListOf(day) else weeks.last() += day
                weeks
            }
            .map { it.first()..it.last() }
    }

    /**
     * [budget] against the planned labour cost of [year]/[month] ([plannedByDate]); null when the
     * budget is off.
     */
    fun status(
        budget: LabourBudget,
        year: Int,
        month: Int,
        firstDayOfWeek: Int,
        plannedByDate: Map<LocalDate, Double>,
        open: OpenShiftsCost = OpenShiftsCost.NONE
    ): LabourBudgetStatus? {
        val monthly = budget.monthly ?: return null
        val inMonth = plannedByDate.filterKeys { it.year == year && it.month.number == month }
        val weekly = budget.weekly
        val weeks = if (weekly == null) emptyList() else weeksOf(year, month, firstDayOfWeek).map { range ->
            val days = daysBetween(range.start, range.endInclusive)
            WeekBudgetUse(
                start = range.start,
                end = range.endInclusive,
                use = BudgetUse(weekly * days / 7.0, inMonth.filterKeys { it in range }.values.sum())
            )
        }
        return LabourBudgetStatus(budget, BudgetUse(monthly, inMonth.values.sum()), weeks, open)
    }

    /** The shifts [status] (and [explainUsed]) prices: assigned, or unassigned demand still to be filled, in the month. */
    private fun pricedShifts(shifts: List<Shift>, year: Int, month: Int): List<Shift> =
        shifts.filter { it.date.isNotEmpty() && (it.userId != null || it.isUnfilledDemand) }
            .filter { LocalDate.parse(it.date).let { d -> d.year == year && d.month.number == month } }

    /**
     * SH-152: the month's planned labour cost against the budget ([LabourBudgetStatus.month]'s
     * used) person by person, as [status] prices the shifts: each person's shifts, the unassigned
     * ones at their estimate (or listed, not counted, when not priced).
     */
    fun explainUsed(year: Int, month: Int, shifts: List<Shift>, estimate: ShiftCostEstimate, shown: Double): Breakdown {
        val priced = pricedShifts(shifts, year, month)
        val terms = buildList {
            priced.filter { it.userId != null }.groupBy { it.userId!! }.entries
                .map { (userId, theirs) ->
                    val kind = when {
                        estimate.isExcluded(userId) -> TermKind.PERSON_NOT_COUNTED
                        estimate.isUnpaid(userId) -> TermKind.PERSON_UNPAID
                        else -> TermKind.PERSON
                    }
                    val sign = if (kind == TermKind.PERSON_NOT_COUNTED) Sign.NOT_COUNTED else Sign.PLUS
                    Term(kind, theirs.sumOf(estimate::costOf), sign, personId = userId, hours = theirs.sumOf(ShiftCostEstimate::hoursOf), shifts = theirs.size)
                }
                .sortedWith(compareByDescending<Term> { it.amount }.thenBy { it.personId })
                .forEach(::add)
            val open = priced.filter { it.userId == null }
            if (open.isNotEmpty()) {
                val hours = open.sumOf(ShiftCostEstimate::hoursOf)
                val rate = estimate.openCostPerHour
                add(
                    if (rate != null) Term(TermKind.OPEN_SHIFTS, open.sumOf(estimate::costOf), hours = hours, shifts = open.size, rate = rate)
                    else Term(TermKind.OPEN_SHIFTS_NOT_PRICED, 0.0, Sign.NOT_COUNTED, hours = hours, shifts = open.size)
                )
            }
        }
        return Breakdown.of(Figure.BUDGET_USED, Side.PLAN, shown, terms)
    }

    /**
     * [budget] against the planned cost of [shifts] (each as [estimate] prices it), unassigned
     * shifts still to be filled at their estimate when there is one.
     */
    fun status(
        budget: LabourBudget,
        year: Int,
        month: Int,
        firstDayOfWeek: Int,
        shifts: List<Shift>,
        estimate: ShiftCostEstimate
    ): LabourBudgetStatus? {
        val inMonth = shifts.filter { it.date.isNotEmpty() }
        return status(
            budget, year, month, firstDayOfWeek,
            inMonth.filter { it.userId != null || it.isUnfilledDemand }
                .groupBy { LocalDate.parse(it.date) }
                .mapValues { (_, day) -> day.sumOf(estimate::costOf) },
            OpenShiftsCost.of(inMonth.filter { LocalDate.parse(it.date).let { d -> d.year == year && d.month.number == month } }, estimate.openCostPerHour)
        )
    }

    private fun daysBetween(start: LocalDate, end: LocalDate): Int =
        generateSequence(start) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= end }.count()
}

/** A labour budget the settings cannot keep: an amount not above zero (or absurdly large). */
class InvalidLabourBudgetException(val amount: Double) : IllegalArgumentException("Invalid labour budget amount: $amount")
